import { useState } from "react";
import { createPortal } from "react-dom";
import { X } from "../../pages/overview/icons";
import { SnippetContextProvider } from "../../pages/docs/snippetContext";
import { AddAgentWizard } from "./AddAgentWizard";

/* The Add-Agent wizard in its modal, shown from the dashboard header and over /welcome. */
export function AddAgentModal({
  open,
  onClose,
  onAdded,
}: {
  open: boolean;
  onClose: () => void;
  onAdded?: (agentId: string) => void;
}) {
  /* Remounts the wizard for "connect another". Reopening the modal needs no bump: the
     subtree is unmounted while closed, so it already comes back fresh. */
  const [runId, setRunId] = useState(0);

  /* No Escape handler and the scrim ignores clicks (OPSS-342): step 1's one-time key
     can't be recovered, so the explicit X is the only way out. */

  /* Portaled into <body> in its own light scope so the wizard is always the light surface
     the design specifies (OPSS-259); `tw-app-light` opts it out of the <html> dark rule. */
  return createPortal(
    <div className="tw-app tw-app-light">
      <div
        aria-hidden
        className="fixed inset-0 z-[60] bg-black/30 transition-opacity duration-200"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />

      <div className="pointer-events-none fixed inset-0 z-[61] flex items-center justify-center p-4">
        <section
          role="dialog"
          aria-modal="true"
          aria-label="Connect your agent"
          aria-hidden={!open}
          /* max-w-2xl (672) around a max-w-lg (512) column — the same content
             width WelcomePage gives the wizard. rounded-md is the 8px overlay
             radius; the old rounded-[5px] was on no scale. */
          className="onboarding onboarding-card ai21-brand relative flex max-h-[calc(100vh-48px)] w-full max-w-2xl flex-col overflow-y-auto rounded-md"
          style={{
            background: "var(--page)",
            fontFamily: "var(--ai21-font-ui)",
            pointerEvents: open ? "auto" : "none",
            opacity: open ? 1 : 0,
            transform: open ? "none" : "translateY(10px) scale(0.98)",
            transition:
              "transform 320ms cubic-bezier(0.22,1,0.36,1), opacity 200ms ease",
            boxShadow: open ? "0 24px 64px rgba(0,0,0,0.25)" : "none",
            visibility: open ? "visible" : "hidden",
          }}
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute right-3 top-3 z-10 text-[var(--fg-3)] transition-colors hover:text-[var(--fg-1)]"
          >
            <X size={20} />
          </button>

          {open && (
            <div className="flex justify-center px-10 py-12">
              <div className="w-full max-w-lg">
                <SnippetContextProvider key={runId}>
                  <AddAgentWizard
                    onFinish={(agentId) => {
                      onAdded?.(agentId);
                      onClose();
                    }}
                    onAddAnother={() => setRunId((n) => n + 1)}
                  />
                </SnippetContextProvider>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>,
    document.body,
  );
}
